/**
 * Login route decision: is this browser ALREADY signed in?
 *
 * WHY THIS EXISTS (incident 2026-10-08, first external tester): /login rendered the sign-in
 * hero even for a browser that held a valid Meridian session, because the page only looked at
 * the in-memory session store (empty on a fresh page load). The user pressed "Sign in" again,
 * which started a second OIDC transaction with return_to=/login; a double-submitted Keycloak
 * form then produced the raw-JSON callback error. Now /login asks the server first
 * (GET /api/v2/me) and goes to "/" when the answer is a real 200.
 *
 * Pure + dependency-free so `npm run test:unit` exercises it with Node's runner. The probe
 * uses plain fetch on purpose, NOT the shared api client / useMe():
 *   - only a genuine 200 may count as "signed in": useMe() used to fall back to MOCK data on a
 *     transport error (it no longer does -- mocks are served only when VITE_USE_MOCKS=true,
 *     lib/api/hooks withMock), and in a mock build it would still answer "signed in" while
 *     the backend is down;
 *   - the shared client's global 401 handler redirects to /auth/login, i.e. it would start
 *     the very login this check is meant to avoid.
 */

/** What the /api/v2/me probe saw. */
export type MeProbeResult =
  | { kind: "ok" } // 200: a live session
  | { kind: "http"; status: number } // 401 (signed out) or any other HTTP status
  | { kind: "transport" }; // network error / backend down

/** What the /login route should do with that result. */
export type LoginRouteDecision = "go_home" | "show_login";

/** Only a real 200 means "already signed in"; everything else shows the sign-in card. */
export function loginRouteDecision(result: MeProbeResult): LoginRouteDecision {
  return result.kind === "ok" ? "go_home" : "show_login";
}

/** Minimal fetch signature so tests can inject a fake. */
export type FetchLike = (
  input: string,
  init?: { credentials?: "include" | "same-origin" | "omit"; headers?: Record<string, string>; signal?: AbortSignal },
) => Promise<{ status: number }>;

/**
 * Ask the BFF whether this browser has a session. Same-origin, cookie included, never throws.
 * `signal` lets the caller abort on unmount.
 */
export async function probeMe(fetchImpl: FetchLike, signal?: AbortSignal): Promise<MeProbeResult> {
  try {
    const res = await fetchImpl("/api/v2/me", {
      credentials: "include",
      headers: { Accept: "application/json" },
      signal,
    });
    return res.status === 200 ? { kind: "ok" } : { kind: "http", status: res.status };
  } catch {
    return { kind: "transport" };
  }
}
